<template>
  <div class="referral-preview">
    <section class="referral-hero" aria-labelledby="referral-title">
      <img
        class="referral-hero__art"
        src="../assets/images/ai/referral-pegasus-hero.png"
        alt="ปีกาซัสสีขาวทองกำลังโผบินเหนือภูเขายามค่ำคืน"
      />
      <div class="referral-hero__shade"></div>

      <header class="referral-header">
        <img
          class="referral-header__logo"
          src="../assets/images/logo2.png"
          alt="UFA Easy888"
        />
        <div class="referral-header__brand">
          <span>UFA EASY</span>
          <strong>888</strong>
        </div>
        <button
          class="icon-button"
          type="button"
          aria-label="ดูเงื่อนไขระบบแนะนำ"
          @click="openRules"
        >
          <i class="fas fa-info-circle" aria-hidden="true"></i>
        </button>
      </header>

      <div class="referral-hero__copy">
        <span class="eyebrow">สิทธิพิเศษสำหรับสมาชิก</span>
        <h1 id="referral-title">ระบบแนะนำ</h1>
        <p>ทุกยอดมีค่า สะสมโบนัสได้โดยไม่มียอดฝากขั้นต่ำ</p>
        <div class="reward-rule">รับ <strong>5%</strong> แบบรายสัปดาห์</div>
      </div>
    </section>

    <main class="referral-main">
      <section
        class="earnings-panel"
        :class="{ 'earnings-panel--loading': isLoading }"
        aria-label="สรุปโบนัสจากระบบแนะนำ"
        aria-live="polite"
      >
        <div class="earnings-panel__primary">
          <span class="stat-label">
            โบนัสรอยืนยันสัปดาห์นี้
            <button type="button" aria-label="ดูที่มาของยอด" @click="openRules">
              <i class="fas fa-info-circle" aria-hidden="true"></i>
            </button>
          </span>
          <strong class="money"
            ><small>฿</small>{{ formattedBonusPending }}</strong
          >
          <span class="stat-meta">
            รอบ <b>{{ weekLabel }}</b> · อัปเดต {{ calculatedAt }} น.
          </span>
        </div>

        <div class="earnings-panel__pending">
          <i
            class="fas fa-fingerprint summary-code-icon"
            aria-hidden="true"
          ></i>
          <span class="stat-label">รหัสที่แนะนำ</span>
          <strong class="money money--pending">{{ referralCount }}</strong>
          <span class="stat-meta">แสดงเฉพาะรหัส ไม่แสดงชื่อหรือรูป</span>
        </div>
      </section>

      <section class="settlement-note" aria-label="กำหนดยืนยันโบนัส">
        <span class="settlement-note__icon"><i class="fas fa-hourglass-half" aria-hidden="true"></i></span>
        <div>
          <strong>พักยอด 7 วันเพื่อความยุติธรรม</strong>
          <span>ตรวจยอดถอนและกิจกรรมจริง ก่อนยืนยันวันที่ {{ confirmationDate }}</span>
        </div>
        <b>T+7</b>
      </section>

      <div v-if="dataError" class="data-alert" role="alert">
        <i class="fas fa-exclamation-circle" aria-hidden="true"></i>
        <span>{{ dataError }}</span>
        <button type="button" @click="loadReferralData">ลองใหม่</button>
      </div>

      <button
        class="line-button"
        :class="{ 'line-button--loading': shareState === 'loading' }"
        type="button"
        :disabled="shareState === 'loading' || isLoading || !inviteReady"
        @click="handleShare"
      >
        <span class="line-button__mark">LINE</span>
        <span>{{ shareButtonLabel }}</span>
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>

      <section class="copy-link-card" aria-labelledby="copy-link-title">
        <div class="copy-link-card__heading">
          <span class="copy-link-card__icon">
            <i class="fas fa-link" aria-hidden="true"></i>
          </span>
          <div>
            <span>ลิงก์แนะนำของคุณ</span>
            <strong id="copy-link-title">คัดลอกแล้วนำไปวางได้ทุกช่องทาง</strong>
          </div>
        </div>

        <div
          class="copy-link-control"
          :class="{ 'copy-link-control--success': copyState === 'success' }"
        >
          <input
            ref="inviteLinkInput"
            :value="inviteLink"
            type="text"
            readonly
            aria-label="ลิงก์แนะนำของคุณ"
            aria-describedby="copy-link-help"
            @focus="$event.target.select()"
            @click="$event.target.select()"
          />
          <button
            type="button"
            :aria-label="copyLabel"
            :disabled="isLoading || !inviteReady"
            @click="copyInviteLink"
          >
            <i
              :class="['fas', copyState === 'success' ? 'fa-check' : 'fa-copy']"
              aria-hidden="true"
            ></i>
            <span>{{ copyLabel }}</span>
          </button>
        </div>

        <p id="copy-link-help">
          <i class="fas fa-hand-pointer" aria-hidden="true"></i>
          กดคัดลอก แล้วแตะค้างในช่องพิมพ์ของ Facebook, LINE หรือแชตอื่นเพื่อวาง
        </p>
      </section>

      <section class="trust-strip" aria-label="ความปลอดภัยและความเป็นส่วนตัว">
        <div>
          <i class="fas fa-shield-alt" aria-hidden="true"></i>
          <span><strong>ปลอดภัย</strong>ระบบ UFA Easy888</span>
        </div>
        <div>
          <i class="fas fa-certificate" aria-hidden="true"></i>
          <span><strong>ตรวจสอบได้</strong>สถานะอัปเดตชัดเจน</span>
        </div>
        <div>
          <i class="fas fa-lock" aria-hidden="true"></i>
          <span><strong>เป็นส่วนตัว</strong>แสดงเฉพาะรหัส</span>
        </div>
      </section>

      <section class="steps-section" aria-labelledby="steps-title">
        <div class="section-heading">
          <div>
            <span class="section-kicker">เริ่มได้ทันที</span>
            <h2 id="steps-title">ง่ายเพียง 3 ขั้นตอน</h2>
          </div>
          <span class="section-count">01—03</span>
        </div>

        <ol class="steps-list">
          <li>
            <span class="step-icon"
              ><i class="fas fa-paper-plane" aria-hidden="true"></i
            ></span>
            <div>
              <strong>ส่งคำแนะนำ</strong
              ><span>ส่งลิงก์หรือข้อความผ่าน LINE</span>
            </div>
            <b>1</b>
          </li>
          <li>
            <span class="step-icon"
              ><i class="fas fa-user-check" aria-hidden="true"></i
            ></span>
            <div>
              <strong>ผู้ถูกแนะนำสมัคร</strong
              ><span>ระบบบันทึกด้วยรหัสเฉพาะ</span>
            </div>
            <b>2</b>
          </li>
          <li>
            <span class="step-icon"
              ><i class="fas fa-gift" aria-hidden="true"></i
            ></span>
            <div>
              <strong>สะสมและรับโบนัส</strong><span>ยืนยันรายสัปดาห์หลังตรวจ T+7</span>
            </div>
            <b>3</b>
          </li>
        </ol>
      </section>

      <section class="codes-section" aria-labelledby="codes-title">
        <div class="section-heading section-heading--codes">
          <div>
            <span class="section-kicker">อัปเดตล่าสุด</span>
            <h2 id="codes-title">รหัสที่แนะนำ</h2>
          </div>
          <button type="button" @click="privacyNotice = !privacyNotice">
            <i class="fas fa-eye-slash" aria-hidden="true"></i>
            ความเป็นส่วนตัว
          </button>
        </div>

        <transition name="notice">
          <p v-if="privacyNotice" class="privacy-notice">
            ระบบซ่อนข้อมูลระบุตัวบุคคลทั้งหมด
            และแสดงเฉพาะรหัสที่ปิดบางส่วนเท่านั้น
          </p>
        </transition>

        <div
          v-if="isLoading"
          class="code-table code-table--loading"
          aria-hidden="true"
        >
          <div v-for="index in 3" :key="index" class="code-row-skeleton">
            <span></span><span></span><span></span>
          </div>
        </div>

        <div v-else-if="!referralCodes.length" class="empty-codes">
          <span><i class="fas fa-fingerprint" aria-hidden="true"></i></span>
          <strong>ยังไม่มีรหัสที่แนะนำ</strong>
          <p>คัดลอกลิงก์หรือส่งผ่าน LINE เพื่อเริ่มต้นได้ทันที</p>
        </div>

        <div v-else class="code-table">
          <button
            v-for="(item, index) in visibleReferralCodes"
            :key="`${item.code}-${index}`"
            class="code-row"
            type="button"
            @click="openCode(item)"
          >
            <span class="code-row__code">{{ item.code }}</span>
            <span :class="['status-pill', `status-pill--${item.tone}`]">
              <i :class="['fas', item.icon]" aria-hidden="true"></i>
              {{ item.status }}
            </span>
            <span class="code-row__reward">
              <small>{{ item.rewardLabel }}</small>
              <strong :class="`reward--${item.tone}`">{{ item.reward }}</strong>
            </span>
            <i
              class="fas fa-chevron-right code-row__arrow"
              aria-hidden="true"
            ></i>
          </button>
        </div>

        <button
          v-if="remainingCodeCount > 0"
          class="load-more-codes"
          type="button"
          @click="showMoreCodes"
        >
          <i class="fas fa-chevron-down" aria-hidden="true"></i>
          แสดงเพิ่มอีก {{ nextCodeBatchSize }} รหัส
        </button>

        <p class="codes-section__caption">
          <i class="fas fa-lock" aria-hidden="true"></i>
          แสดงเฉพาะรหัสเพื่อความเป็นส่วนตัว
        </p>
      </section>

      <button class="rules-button" type="button" @click="openRules">
        <i class="fas fa-file-contract" aria-hidden="true"></i>
        <span>ดูเงื่อนไขการรับโบนัส</span>
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </main>

    <transition name="toast">
      <div v-if="toast" class="referral-toast" role="status">
        <i class="fas fa-check-circle" aria-hidden="true"></i>
        {{ toast }}
      </div>
    </transition>

    <transition name="sheet">
      <div v-if="sheet" class="sheet-layer" @click.self="closeSheet">
        <section
          class="bottom-sheet"
          role="dialog"
          aria-modal="true"
          :aria-label="sheet.title"
        >
          <div class="bottom-sheet__handle"></div>
          <button
            class="bottom-sheet__close"
            type="button"
            aria-label="ปิด"
            @click="closeSheet"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>

          <template v-if="sheet.type === 'share'">
            <span class="sheet-icon sheet-icon--line"
              ><i class="fab fa-line" aria-hidden="true"></i
            ></span>
            <h2>{{ sheet.title }}</h2>
            <p>
              ใน LINE LIFF ระบบจะเปิดรายชื่อผู้รับผ่าน
              <b>shareTargetPicker</b> โดยไม่ดึงข้อมูลรายชื่อกลับมาเก็บไว้
            </p>
            <div class="share-message-preview">
              <img
                src="../assets/images/ai/referral-pegasus-hero.png"
                alt="ปีกาซัสสีขาวทอง"
              />
              <div>
                <strong>UFA Easy888 ระบบแนะนำ</strong>
                <span>ไม่มีขั้นต่ำ สะสม 5% พร้อมตรวจยอดแบบ T+7</span>
              </div>
            </div>
            <button
              class="sheet-primary"
              type="button"
              @click="finishSharePreview"
            >
              <span class="line-button__mark">LINE</span>
              จำลองเลือกผู้รับใน LINE
            </button>
          </template>

          <template v-else-if="sheet.type === 'rules'">
            <span class="sheet-icon"
              ><i class="fas fa-file-contract" aria-hidden="true"></i
            ></span>
            <h2>{{ sheet.title }}</h2>
            <ul class="rules-list">
              <li>
                <i class="fas fa-check" aria-hidden="true"></i
                ><span>ทุกยอดฝากมีสิทธิ์สะสมโบนัส 5% โดยไม่กำหนดยอดฝากขั้นต่ำ</span>
              </li>
              <li>
                <i class="fas fa-check" aria-hidden="true"></i
                ><span>คำนวณรายสัปดาห์ และหักยอดถอนตลอดช่วงพักยอด 7 วัน</span>
              </li>
              <li>
                <i class="fas fa-check" aria-hidden="true"></i
                ><span>ต้องมีกิจกรรมการเล่นที่ตรวจสอบได้ก่อนยืนยันโบนัส</span>
              </li>
              <li>
                <i class="fas fa-check" aria-hidden="true"></i
                ><span>โบนัสทุกยอดปัดเป็น 2 ตำแหน่ง และเติมเครดิตให้อัตโนมัติ</span>
              </li>
            </ul>
            <button
              class="sheet-primary sheet-primary--orange"
              type="button"
              @click="closeSheet"
            >
              เข้าใจแล้ว
            </button>
          </template>

          <template v-else>
            <span class="sheet-icon"
              ><i class="fas fa-fingerprint" aria-hidden="true"></i
            ></span>
            <h2>{{ sheet.title }}</h2>
            <div class="code-detail">
              <span>สถานะ</span><strong>{{ sheet.item.status }}</strong>
              <span>โบนัส</span><strong>{{ sheet.item.reward }}</strong>
              <span>อัปเดต</span><strong>{{ sheet.item.updated }}</strong>
            </div>
            <p class="sheet-privacy">
              <i class="fas fa-eye-slash" aria-hidden="true"></i>
              ไม่มีการแสดงชื่อ รูป หรือข้อมูลส่วนบุคคล
            </p>
          </template>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import liffService from "@/services/liff";
import { loadReferralDashboard } from "@/services/referral";

export default {
  name: "ReferralPreview",
  created() {
    this.loadReferralData();
  },
  beforeDestroy() {
    window.clearTimeout(this.copyResetTimer);
    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.localPreviewTimer);
  },
  data() {
    return {
      inviteLink: "",
      copyLabel: "คัดลอก",
      copyState: "idle",
      privacyNotice: false,
      shareState: "idle",
      sheet: null,
      toast: "",
      isLoading: true,
      dataError: "",
      bonusPending: 0,
      weekLabel: "—",
      confirmationDate: "—",
      calculatedAt: "—",
      referralCodes: [],
      visibleCodeLimit: 50
    };
  },
  computed: {
    referralCount() {
      return this.referralCodes.length;
    },
    visibleReferralCodes() {
      return this.referralCodes.slice(0, this.visibleCodeLimit);
    },
    remainingCodeCount() {
      return Math.max(this.referralCodes.length - this.visibleCodeLimit, 0);
    },
    nextCodeBatchSize() {
      return Math.min(this.remainingCodeCount, 50);
    },
    formattedBonusPending() {
      return this.formatMoney(this.bonusPending);
    },
    inviteReady() {
      return Boolean(this.inviteLink);
    },
    shareButtonLabel() {
      if (this.isLoading) return "กำลังโหลดข้อมูล...";
      if (!this.inviteReady) return "ยังไม่พบรหัสสำหรับแนะนำ";
      return this.shareState === "loading"
        ? "กำลังเตรียม LINE..."
        : "ส่งคำแนะนำผ่าน LINE";
    }
  },
  methods: {
    formatMoney(value) {
      return Number(value || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    applyDashboard(dashboard) {
      this.bonusPending = dashboard.bonusPending;
      this.weekLabel = dashboard.weekLabel;
      this.confirmationDate = dashboard.confirmationDate;
      this.calculatedAt = dashboard.calculatedAt;
      this.visibleCodeLimit = 50;
      this.inviteLink = dashboard.owner.code
        ? `https://ufaeasy888.me/r/${encodeURIComponent(dashboard.owner.code)}`
        : "";
      this.referralCodes = dashboard.rows.map(item => ({
        code: item.maskedCode,
        status: item.hasPendingBonus ? "กำลังสะสม" : "ยังไม่มียอดสัปดาห์นี้",
        tone: item.hasPendingBonus ? "success" : "neutral",
        icon: item.hasPendingBonus ? "fa-hourglass-half" : "fa-minus-circle",
        rewardLabel: "ยอดรอยืนยัน",
        reward: item.hasPendingBonus
          ? `฿${this.formatMoney(item.bonusPending)}`
          : "—",
        updated: `คำนวณล่าสุด ${dashboard.calculatedAt} น.`
      }));
    },
    loadLocalPreview() {
      this.localPreviewTimer = window.setTimeout(() => {
        this.applyDashboard({
          owner: { code: "DEV001" },
          bonusPending: 225.15,
          weekLabel: "21/08–27/08",
          confirmationDate: "04/09/2026",
          calculatedAt: "10:42",
          rows: [
            { maskedCode: "UFA•••12", bonusPending: 150, hasPendingBonus: true },
            { maskedCode: "UFA•••04", bonusPending: 0, hasPendingBonus: false },
            { maskedCode: "UFA•••71", bonusPending: 75.15, hasPendingBonus: true },
            { maskedCode: "UFA•••55", bonusPending: 0, hasPendingBonus: false }
          ]
        });
        this.isLoading = false;
      }, 280);
    },
    async loadReferralData() {
      this.isLoading = true;
      this.dataError = "";

      const isLocalPreview =
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "localhost";
      if (isLocalPreview) {
        this.loadLocalPreview();
        return;
      }

      const profile = liffService.profile;
      if (!profile || !profile.userId) {
        this.isLoading = false;
        this.dataError =
          "ไม่สามารถอ่านข้อมูลสมาชิกจาก LINE ได้ กรุณาเปิดหน้านี้ผ่าน LINE อีกครั้ง";
        return;
      }

      try {
        const dashboard = await loadReferralDashboard(profile.userId);
        this.applyDashboard(dashboard);
        if (!dashboard.owner.code) {
          this.dataError =
            "ยังไม่พบรหัสสมาชิกสำหรับสร้างลิงก์ กรุณาติดต่อแอดมิน";
        }
      } catch (error) {
        this.dataError =
          error && error.message === "REFERRAL_OWNER_NOT_FOUND"
            ? "ยังไม่พบข้อมูลสมาชิก กรุณาลงทะเบียนหรือเข้าสู่ระบบก่อน"
            : "โหลดข้อมูลระบบแนะนำไม่สำเร็จ กรุณาลองอีกครั้ง";
      } finally {
        this.isLoading = false;
      }
    },
    buildShareMessage() {
      return [
        {
          type: "flex",
          altText: "UFA Easy888 ระบบแนะนำ",
          contents: {
            type: "bubble",
            hero: {
              type: "image",
              url:
                "https://line-ufaeasy2.web.app/images/line-header.jpg",
              size: "full",
              aspectRatio: "20:13",
              aspectMode: "cover"
            },
            body: {
              type: "box",
              layout: "vertical",
              spacing: "md",
              contents: [
                {
                  type: "text",
                  text: "UFA Easy888 ระบบแนะนำ",
                  weight: "bold",
                  size: "lg",
                  wrap: true
                },
                {
                  type: "text",
                  text: "ทุกยอดมีค่า ไม่มีขั้นต่ำ สะสมโบนัส 5% แบบรายสัปดาห์ พร้อมตรวจยอด T+7",
                  color: "#666666",
                  size: "sm",
                  wrap: true
                }
              ]
            },
            footer: {
              type: "box",
              layout: "vertical",
              contents: [
                {
                  type: "button",
                  style: "primary",
                  color: "#06C755",
                  action: {
                    type: "uri",
                    label: "ดูรายละเอียด",
                    uri: this.inviteLink
                  }
                }
              ]
            }
          }
        }
      ];
    },
    async handleShare() {
      if (!this.inviteReady) return;
      this.shareState = "loading";
      const isLocalPreview =
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname === "localhost";

      if (isLocalPreview || !window.liff || !liffService.isInitialized) {
        window.setTimeout(() => {
          this.shareState = "idle";
          this.sheet = {
            type: "share",
            title: "พร้อมส่งคำแนะนำผ่าน LINE"
          };
        }, 500);
        return;
      }

      try {
        await liffService.shareTargetPicker(this.buildShareMessage());
        this.shareState = "idle";
        this.showToast("ส่งคำแนะนำเรียบร้อยแล้ว");
      } catch (error) {
        this.shareState = "idle";
        this.copyState = "idle";
        this.copyLabel = "คัดลอกแทน";
        this.showToast("ยังเปิด LINE ไม่สำเร็จ ลองคัดลอกลิงก์แทน");
      }
    },
    async copyInviteLink() {
      if (!this.inviteReady) return;
      let copied = false;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(this.inviteLink);
          copied = true;
        }
      } catch (error) {
        copied = false;
      }

      if (!copied) {
        try {
          await this.$copyText(this.inviteLink);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }

      if (!copied && this.$refs.inviteLinkInput) {
        const input = this.$refs.inviteLinkInput;
        input.focus();
        input.select();

        try {
          copied = document.execCommand("copy");
        } catch (error) {
          copied = false;
        }
      }

      window.clearTimeout(this.copyResetTimer);

      if (copied) {
        this.copyState = "success";
        this.copyLabel = "คัดลอกแล้ว";
        this.showToast("คัดลอกลิงก์แล้ว — นำไปวางได้ทันที");
        this.copyResetTimer = window.setTimeout(() => {
          this.copyState = "idle";
          this.copyLabel = "คัดลอก";
        }, 2800);
        return;
      }

      this.copyState = "manual";
      this.copyLabel = "แตะค้างเพื่อคัดลอก";
      this.showToast("เลือกข้อความแล้ว แตะค้างและเลือก “คัดลอก”");
    },
    finishSharePreview() {
      this.closeSheet();
      this.showToast("ตัวอย่าง: ส่งคำแนะนำเรียบร้อยแล้ว");
    },
    openRules() {
      this.sheet = { type: "rules", title: "เงื่อนไขการรับโบนัส" };
    },
    openCode(item) {
      this.sheet = { type: "code", title: item.code, item };
    },
    showMoreCodes() {
      this.visibleCodeLimit += 50;
    },
    closeSheet() {
      this.sheet = null;
    },
    showToast(message) {
      this.toast = message;
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => {
        this.toast = "";
      }, 2600);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../assets/css/all.css";

$navy: #06111f;
$navy-deep: #020a14;
$surface: #111820;
$surface-soft: #17202a;
$orange: #ff8a24;
$orange-soft: #ffb154;
$line-green: #06c755;
$text: #f7f8fb;
$muted: #9aa4b2;
$border: rgba(255, 255, 255, 0.11);

.referral-preview {
  width: 100%;
  max-width: 430px;
  min-height: 100vh;
  margin: 0 auto;
  overflow: hidden;
  color: $text;
  background: radial-gradient(
      circle at 95% 11%,
      rgba(255, 138, 36, 0.12),
      transparent 26%
    ),
    linear-gradient(180deg, #071321 0%, $navy-deep 100%);
  font-family: "Athiti", "Kanit", sans-serif;
  -webkit-font-smoothing: antialiased;
}

.referral-hero {
  position: relative;
  height: 340px;
  overflow: hidden;
}

.referral-hero__art,
.referral-hero__shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.referral-hero__art {
  object-fit: cover;
  object-position: 55% center;
}

.referral-hero__shade {
  background: linear-gradient(
      90deg,
      rgba(2, 10, 20, 0.96) 0%,
      rgba(2, 10, 20, 0.72) 42%,
      rgba(2, 10, 20, 0.08) 76%
    ),
    linear-gradient(
      180deg,
      rgba(2, 10, 20, 0.2) 0%,
      transparent 54%,
      $navy 100%
    );
}

.referral-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  min-height: 74px;
  padding: max(12px, env(safe-area-inset-top)) 18px 10px;
}

.referral-header__logo {
  width: 52px;
  height: 52px;
  object-fit: contain;
  filter: drop-shadow(0 6px 14px rgba(255, 138, 36, 0.2));
}

.referral-header__brand {
  display: grid;
  margin-left: 7px;
  color: $orange-soft;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: 0.04em;
}

.referral-header__brand strong {
  font-size: 15px;
}

.icon-button {
  display: grid;
  width: 44px;
  height: 44px;
  margin-left: auto;
  place-items: center;
  border: 1px solid rgba(255, 177, 84, 0.42);
  border-radius: 50%;
  color: $orange-soft;
  background: rgba(6, 17, 31, 0.7);
}

.referral-hero__copy {
  position: relative;
  z-index: 2;
  width: 57%;
  padding: 28px 0 0 22px;
}

.eyebrow,
.section-kicker {
  display: block;
  color: $orange-soft;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
}

.referral-hero__copy h1 {
  margin: 8px 0 4px;
  color: $text;
  font-family: "Mali", "Kanit", sans-serif;
  font-size: clamp(31px, 9vw, 38px);
  font-weight: 700;
  letter-spacing: -0.04em;
  white-space: nowrap;
}

.referral-hero__copy p {
  max-width: 190px;
  margin: 0;
  color: rgba(247, 248, 251, 0.78);
  font-size: 16px;
  line-height: 1.35;
}

.reward-rule {
  margin-top: 15px;
  color: $text;
  font-size: 16px;
  font-weight: 600;
}

.reward-rule strong {
  margin: 0 4px;
  color: $orange;
  font-family: "Poppins", sans-serif;
  font-size: 24px;
}

.referral-main {
  position: relative;
  z-index: 3;
  padding: 0 16px calc(40px + env(safe-area-inset-bottom));
}

.earnings-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr);
  min-height: 166px;
  margin-top: -30px;
  overflow: hidden;
  border: 1px solid rgba(255, 138, 36, 0.78);
  border-radius: 22px;
  background: linear-gradient(
    135deg,
    rgba(19, 22, 25, 0.98),
    rgba(8, 12, 17, 0.96)
  );
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35), 0 0 24px rgba(255, 138, 36, 0.08);
}

.earnings-panel__primary,
.earnings-panel__pending {
  display: flex;
  min-width: 0;
  flex-direction: column;
  justify-content: center;
  padding: 20px 16px;
}

.earnings-panel__primary {
  border-right: 1px solid $border;
}

.earnings-panel__pending {
  position: relative;
  padding-left: 14px;
}

.summary-code-icon {
  position: absolute;
  top: 10px;
  right: 8px;
  color: rgba(255, 138, 36, 0.35);
  font-size: 38px;
  filter: drop-shadow(0 0 12px rgba(255, 138, 36, 0.22));
}

.earnings-panel--loading .money,
.earnings-panel--loading .stat-meta {
  opacity: 0.38;
}

.stat-label {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #c2c8d0;
  font-size: 14px;
  line-height: 1.2;
}

.stat-label button {
  color: #c2c8d0;
}

.money {
  display: block;
  margin: 9px 0 4px;
  color: $orange;
  font-family: "Poppins", sans-serif;
  font-size: clamp(28px, 9vw, 38px);
  line-height: 1.08;
  letter-spacing: -0.04em;
  white-space: nowrap;
}

.money small {
  margin-right: 2px;
  font-size: 0.58em;
}

.money--pending {
  color: $text;
  font-size: clamp(21px, 6vw, 28px);
}

.stat-meta {
  color: $muted;
  font-size: 12px;
  line-height: 1.3;
}

.stat-meta b {
  color: $orange;
  font-family: "Poppins", sans-serif;
}

.settlement-note {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  margin-top: 12px;
  padding: 13px 14px;
  border: 1px solid rgba(255, 178, 92, 0.22);
  border-radius: 17px;
  color: #f4f5f7;
  background:
    linear-gradient(100deg, rgba(255, 138, 36, 0.12), rgba(255, 138, 36, 0.025)),
    rgba(15, 18, 22, 0.9);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.035);
}

.settlement-note__icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 14px;
  color: $orange-soft;
  background: rgba(255, 138, 36, 0.13);
}

.settlement-note div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.settlement-note strong {
  font-size: 13px;
  line-height: 1.2;
}

.settlement-note span:not(.settlement-note__icon) {
  color: $muted;
  font-size: 10px;
  line-height: 1.4;
}

.settlement-note > b {
  padding: 6px 8px;
  border-radius: 999px;
  color: #0d0f12;
  background: $orange-soft;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
}

.line-button,
.sheet-primary {
  display: grid;
  grid-template-columns: 46px 1fr 24px;
  align-items: center;
  width: 100%;
  min-height: 66px;
  margin-top: 18px;
  padding: 8px 18px;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(
    135deg,
    #12b94f 0%,
    $line-green 55%,
    #0cad47 100%
  );
  box-shadow: 0 12px 30px rgba(6, 199, 85, 0.25);
  font-family: "Mali", "Kanit", sans-serif;
  font-size: 18px;
  font-weight: 700;
}

.line-button:disabled {
  cursor: wait;
  opacity: 0.72;
}

.data-alert {
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  margin-top: 12px;
  gap: 8px;
  padding: 11px 12px;
  border: 1px solid rgba(255, 138, 36, 0.3);
  border-radius: 14px;
  color: #e5e8ed;
  background: rgba(255, 138, 36, 0.08);
  font-size: 11px;
  line-height: 1.4;
}

.data-alert i,
.data-alert button {
  color: $orange-soft;
}

.data-alert button {
  min-height: 40px;
  padding: 0 6px;
  font-weight: 700;
  white-space: nowrap;
}

.line-button__mark {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 50%;
  color: $line-green;
  background: #fff;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  font-weight: 700;
}

.copy-link-card {
  margin-top: 12px;
  padding: 14px;
  border: 1px solid $border;
  border-radius: 18px;
  background: rgba(17, 24, 32, 0.88);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.2);
}

.copy-link-card__heading {
  display: flex;
  align-items: center;
  gap: 10px;
}

.copy-link-card__icon {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(255, 177, 84, 0.34);
  border-radius: 50%;
  color: $orange-soft;
  background: rgba(255, 138, 36, 0.08);
}

.copy-link-card__heading > div {
  display: grid;
  min-width: 0;
}

.copy-link-card__heading span:not(.copy-link-card__icon) {
  color: $orange-soft;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.copy-link-card__heading strong {
  color: $text;
  font-size: 14px;
  line-height: 1.25;
}

.copy-link-control {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  min-height: 48px;
  margin-top: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 12px;
  background: rgba(2, 10, 20, 0.88);
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.copy-link-control--success {
  border-color: rgba(34, 197, 94, 0.5);
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.08);
}

.copy-link-control input {
  min-width: 0;
  padding: 0 12px;
  overflow: hidden;
  border: 0;
  outline: 0;
  color: #dce2e9;
  background: transparent;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-link-control input::selection {
  color: #08120c;
  background: #86efac;
}

.copy-link-control button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 108px;
  flex: 0 0 108px;
  gap: 7px;
  padding: 0 12px;
  color: #101418;
  background: $orange-soft;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transition: color 180ms ease, background 180ms ease;
}

.copy-link-control button:disabled {
  cursor: not-allowed;
  opacity: 0.52;
}

.copy-link-control--success button {
  color: #05260f;
  background: #54db7e;
}

.copy-link-card > p {
  display: flex;
  align-items: flex-start;
  margin: 9px 2px 0;
  gap: 7px;
  color: #8994a2;
  font-size: 10px;
  line-height: 1.45;
}

.copy-link-card > p i {
  margin-top: 2px;
  color: $orange;
}

.trust-strip {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 14px 0 28px;
  padding: 15px 0;
  border-top: 1px solid $border;
  border-bottom: 1px solid $border;
}

.trust-strip > div {
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border-right: 1px solid $border;
}

.trust-strip > div:first-child {
  padding-left: 0;
}

.trust-strip > div:last-child {
  padding-right: 0;
  border-right: 0;
}

.trust-strip i {
  color: $orange;
  font-size: 19px;
}

.trust-strip span {
  color: #7f8a98;
  font-size: 9px;
  line-height: 1.25;
}

.trust-strip strong {
  display: block;
  color: #d8dce2;
  font-size: 10px;
}

.steps-section,
.codes-section {
  padding: 4px 0 27px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-heading h2 {
  margin: 4px 0 0;
  color: $text;
  font-family: "Mali", "Kanit", sans-serif;
  font-size: 22px;
  line-height: 1.2;
}

.section-count {
  color: #556170;
  font-family: "Poppins", sans-serif;
  font-size: 11px;
}

.steps-list {
  position: relative;
  display: grid;
}

.steps-list::before {
  position: absolute;
  top: 24px;
  bottom: 24px;
  left: 23px;
  width: 1px;
  background: linear-gradient($orange, rgba(255, 138, 36, 0.12));
  content: "";
}

.steps-list li {
  position: relative;
  display: grid;
  grid-template-columns: 48px 1fr 28px;
  align-items: center;
  min-height: 76px;
  gap: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.steps-list li:last-child {
  border-bottom: 0;
}

.step-icon {
  position: relative;
  z-index: 1;
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 1px solid rgba(255, 138, 36, 0.4);
  border-radius: 50%;
  color: $orange;
  background: $navy;
}

.steps-list li > div {
  display: grid;
}

.steps-list li strong {
  font-size: 15px;
}

.steps-list li span:not(.step-icon) {
  color: $muted;
  font-size: 12px;
}

.steps-list li > b {
  color: #596574;
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  text-align: right;
}

.section-heading--codes {
  align-items: center;
}

.section-heading--codes button {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  gap: 6px;
  color: #8d97a4;
  font-size: 11px;
}

.privacy-notice {
  margin: -4px 0 13px;
  padding: 10px 12px;
  border-left: 2px solid $orange;
  color: #aab2bd;
  background: rgba(255, 138, 36, 0.07);
  font-size: 12px;
  line-height: 1.45;
}

.code-table {
  overflow: hidden;
  border: 1px solid $border;
  border-radius: 18px;
  background: rgba(18, 24, 31, 0.92);
}

.code-row-skeleton {
  display: grid;
  grid-template-columns: 0.9fr 1.15fr 0.7fr;
  align-items: center;
  min-height: 67px;
  gap: 12px;
  padding: 9px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.code-row-skeleton:last-child {
  border-bottom: 0;
}

.code-row-skeleton span {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06),
    rgba(255, 255, 255, 0.13),
    rgba(255, 255, 255, 0.06)
  );
  background-size: 200% 100%;
  animation: referral-shimmer 1.2s linear infinite;
}

.code-row-skeleton span:last-child {
  width: 70%;
  justify-self: end;
}

.empty-codes {
  display: grid;
  justify-items: center;
  padding: 26px 18px;
  border: 1px solid $border;
  border-radius: 18px;
  color: #cad0d8;
  background: rgba(18, 24, 31, 0.92);
  text-align: center;
}

.empty-codes > span {
  display: grid;
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
  place-items: center;
  border-radius: 50%;
  color: $orange;
  background: rgba(255, 138, 36, 0.1);
  font-size: 19px;
}

.empty-codes strong {
  font-size: 15px;
}

.empty-codes p {
  max-width: 250px;
  margin: 4px 0 0;
  color: $muted;
  font-size: 11px;
  line-height: 1.45;
}

@keyframes referral-shimmer {
  to {
    background-position: -200% 0;
  }
}

.code-row {
  display: grid;
  grid-template-columns:
    minmax(88px, 0.9fr) minmax(100px, 1.15fr) minmax(60px, 0.7fr)
    12px;
  align-items: center;
  width: 100%;
  min-height: 67px;
  gap: 6px;
  padding: 9px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  color: $text;
  text-align: left;
}

.code-row:last-child {
  border-bottom: 0;
}

.code-row__code {
  overflow: hidden;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.status-pill--success {
  color: #62db83;
  background: rgba(34, 197, 94, 0.14);
}

.status-pill--pending {
  color: #ffae42;
  background: rgba(245, 158, 11, 0.14);
}

.status-pill--neutral {
  color: #b6bdc7;
  background: rgba(255, 255, 255, 0.07);
}

.code-row__reward {
  display: grid;
  justify-items: end;
  line-height: 1.15;
}

.code-row__reward small {
  color: #7d8794;
  font-size: 9px;
}

.code-row__reward strong {
  font-family: "Poppins", sans-serif;
  font-size: 12px;
}

.reward--success {
  color: #39d06e;
}

.reward--pending {
  color: #ff9c2c;
}

.reward--neutral {
  color: #7d8794;
}

.code-row__arrow {
  color: #798390;
  font-size: 10px;
}

.codes-section__caption {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  gap: 7px;
  color: #74808e;
  font-size: 11px;
}

.load-more-codes {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  margin-top: 10px;
  gap: 8px;
  border: 1px solid rgba(255, 138, 36, 0.3);
  border-radius: 14px;
  color: $orange-soft;
  background: rgba(255, 138, 36, 0.07);
  font-size: 12px;
  font-weight: 700;
}

.rules-button {
  display: grid;
  grid-template-columns: 24px 1fr 16px;
  align-items: center;
  width: 100%;
  min-height: 54px;
  gap: 8px;
  padding: 0 14px;
  border-top: 1px solid $border;
  border-bottom: 1px solid $border;
  color: #b1b8c2;
  font-size: 14px;
  text-align: left;
}

.rules-button i:first-child {
  color: $orange;
}

.referral-toast {
  position: fixed;
  z-index: 40;
  right: 18px;
  bottom: calc(20px + env(safe-area-inset-bottom));
  left: 18px;
  display: flex;
  align-items: center;
  max-width: 390px;
  min-height: 52px;
  margin: 0 auto;
  gap: 10px;
  padding: 12px 16px;
  border: 1px solid rgba(34, 197, 94, 0.35);
  border-radius: 14px;
  color: #dff7e7;
  background: rgba(12, 44, 27, 0.98);
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.44);
}

.referral-toast i {
  color: #3bd46e;
}

.sheet-layer {
  position: fixed;
  z-index: 50;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 14px;
  background: rgba(0, 6, 12, 0.74);
  backdrop-filter: blur(8px);
}

.bottom-sheet {
  position: relative;
  width: 100%;
  max-width: 414px;
  padding: 14px 18px calc(22px + env(safe-area-inset-bottom));
  border: 1px solid $border;
  border-radius: 24px 24px 18px 18px;
  background: #111923;
  box-shadow: 0 -20px 55px rgba(0, 0, 0, 0.5);
}

.bottom-sheet__handle {
  width: 42px;
  height: 4px;
  margin: 0 auto 18px;
  border-radius: 999px;
  background: #47515d;
}

.bottom-sheet__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: #aab2bd;
}

.sheet-icon {
  display: grid;
  width: 50px;
  height: 50px;
  margin-bottom: 12px;
  place-items: center;
  border-radius: 15px;
  color: $orange;
  background: rgba(255, 138, 36, 0.12);
  font-size: 21px;
}

.sheet-icon--line {
  color: $line-green;
  background: rgba(6, 199, 85, 0.12);
}

.bottom-sheet h2 {
  margin: 0 0 8px;
  font-family: "Mali", "Kanit", sans-serif;
  font-size: 20px;
}

.bottom-sheet > p {
  color: $muted;
  font-size: 13px;
  line-height: 1.55;
}

.share-message-preview {
  display: grid;
  grid-template-columns: 88px 1fr;
  min-height: 88px;
  margin: 16px 0;
  overflow: hidden;
  border: 1px solid $border;
  border-radius: 14px;
  background: #0c131b;
}

.share-message-preview img {
  width: 88px;
  height: 100%;
  object-fit: cover;
  object-position: 70% center;
}

.share-message-preview div {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 12px;
}

.share-message-preview strong {
  font-size: 14px;
}

.share-message-preview span {
  margin-top: 3px;
  color: $muted;
  font-size: 11px;
}

.sheet-primary {
  grid-template-columns: 42px 1fr;
  min-height: 58px;
  margin-top: 12px;
  border-radius: 16px;
  font-size: 15px;
}

.sheet-primary--orange {
  display: block;
  color: #111;
  background: linear-gradient(135deg, $orange-soft, $orange);
  box-shadow: 0 12px 30px rgba(255, 138, 36, 0.18);
}

.rules-list {
  display: grid;
  margin: 15px 0;
  gap: 12px;
}

.rules-list li {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 8px;
  color: #c1c7cf;
  font-size: 13px;
  line-height: 1.4;
}

.rules-list i {
  color: $orange;
}

.code-detail {
  display: grid;
  grid-template-columns: 1fr auto;
  margin: 16px 0;
  overflow: hidden;
  border: 1px solid $border;
  border-radius: 14px;
}

.code-detail span,
.code-detail strong {
  min-height: 46px;
  padding: 12px 14px;
  border-bottom: 1px solid $border;
  font-size: 13px;
}

.code-detail span {
  color: $muted;
}

.code-detail strong {
  color: $text;
  text-align: right;
}

.code-detail > :nth-last-child(-n + 2) {
  border-bottom: 0;
}

.sheet-privacy {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  color: #9ca6b3 !important;
  background: rgba(255, 255, 255, 0.04);
}

.notice-enter-active,
.notice-leave-active,
.toast-enter-active,
.toast-leave-active,
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity 180ms ease, transform 220ms ease;
}

.notice-enter,
.notice-leave-to,
.toast-enter,
.toast-leave-to,
.sheet-enter,
.sheet-leave-to {
  opacity: 0;
}

.toast-enter,
.toast-leave-to {
  transform: translateY(12px);
}

.sheet-enter .bottom-sheet,
.sheet-leave-to .bottom-sheet {
  transform: translateY(28px);
}

button:focus-visible {
  outline: 3px solid rgba(255, 177, 84, 0.55);
  outline-offset: 2px;
}

@media (max-width: 360px) {
  .referral-header {
    padding-right: 14px;
    padding-left: 14px;
  }

  .referral-hero__copy {
    padding-left: 18px;
  }

  .referral-main {
    padding-right: 12px;
    padding-left: 12px;
  }

  .money {
    font-size: 29px;
  }

  .line-button {
    font-size: 16px;
  }

  .code-row {
    grid-template-columns: 84px 1fr 58px 10px;
    padding-right: 9px;
    padding-left: 9px;
  }

  .status-pill {
    padding-right: 6px;
    padding-left: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}
</style>
